/**
 * A paid session that survives a renderer reload.
 *
 * Session state lives only in the zustand store, so a reload (main's
 * recoverRenderer after `render-process-gone`, or an operator restarting the app
 * when the camera hangs) drops a guest who has already paid back to Welcome, and
 * they pay again. 09-19..28: 25 of the 31 paid, unfinished sessions with no
 * "Session reset" line in their log ended exactly like that (the booth's next
 * session carried a new appLaunchTime).
 *
 * While a session is settled and not completed, the part needed to carry on is
 * kept: who, which project, the payment, and the choices made before paying.
 * Welcome offers it back to whoever is at the booth: after a crash the paying
 * guest is nearly always the next person there (UD Djaya JAKAL 2026-10-29..31: in
 * 6 of 8 lost paid sessions the next session started 27–107 s later, mostly at
 * the same price, and one guest paid twice). Captured photos are blob URLs that die
 * with the renderer, so a resumed guest shoots again; the point is not to charge
 * them twice. Every deliberate reset clears it (cleanupSession), as does
 * completion.
 *
 * It is offered ONLY for a quick, automatic failure, never after an operator
 * stepped in (review, 09-28): a guest may already have left or been refunded.
 * - sessionStorage, not localStorage: it survives the booth's own recovery,
 *   which reloads the SAME window (recoverRenderer after a renderer crash, the
 *   hang watchdog), but not the app being closed or restarted, which is an
 *   operator's doing.
 * - At most CHECKPOINT_MAX_AGE_MS since the session last changed. An offer nobody
 *   takes up within that window is logged on the paid session as not resumed, so
 *   the team can refund before the guest has to ask.
 * - Behind the RESUME_PAID_SESSION_FLAG feature flag, off unless enabled.
 */

export const CHECKPOINT_KEY = 'dreambooth.paidSessionCheckpoint.v1'
/** Longer than this since the session last changed is no longer a quick failure. */
export const CHECKPOINT_MAX_AGE_MS = 10 * 60 * 1000
/** Feature flag (Studio registry); unknown keys resolve to off. */
export const RESUME_PAID_SESSION_FLAG = 'resume_paid_session'

export interface SessionCheckpoint {
  v: 1
  savedAt: number
  sessionId: string
  projectId: string
  userId: string
  eventId?: string
  mode?: string
  galleryKey?: string
  startTime?: string | null
  transaction: Record<string, unknown>
  userPreferences: Record<string, unknown>
}

/** The fields of the session store this module reads. */
export interface CheckpointSource {
  id?: string
  projectId?: string
  userId?: string
  eventId?: string
  mode?: string
  galleryKey?: string
  timeline: { startTime?: Date | string | null; isCompleted?: boolean }
  // object, not Record<string, unknown>: the store's interfaces have no index signature.
  transaction: { status?: string } & object
  userPreferences: object
}

export interface KeyValueStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

function defaultStore(): KeyValueStore | null {
  try {
    return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null
  } catch {
    return null
  }
}

/** A checkpoint for this state, or null when there is nothing worth keeping. */
export function buildCheckpoint(s: CheckpointSource, now = Date.now()): SessionCheckpoint | null {
  if (!s.id || !s.projectId) return null
  if (s.transaction?.status !== 'settlement') return null
  if (s.timeline?.isCompleted) return null
  const startTime = s.timeline?.startTime
  return {
    v: 1,
    savedAt: now,
    sessionId: s.id,
    projectId: s.projectId,
    userId: s.userId ?? '',
    ...(s.eventId ? { eventId: s.eventId } : {}),
    ...(s.mode ? { mode: s.mode } : {}),
    ...(s.galleryKey ? { galleryKey: s.galleryKey } : {}),
    startTime: startTime ? new Date(startTime).toISOString() : null,
    // Plain JSON copies: the payment and the pre-payment choices (frame, filter,
    // packages, recording type). Nothing here is a blob URL.
    transaction: JSON.parse(JSON.stringify(s.transaction ?? {})),
    userPreferences: JSON.parse(JSON.stringify(s.userPreferences ?? {}))
  }
}

export function readCheckpoint(store = defaultStore()): SessionCheckpoint | null {
  if (!store) return null
  try {
    const raw = store.getItem(CHECKPOINT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as SessionCheckpoint
    return parsed && parsed.v === 1 && parsed.sessionId && parsed.projectId ? parsed : null
  } catch {
    return null
  }
}

export function clearCheckpoint(store = defaultStore()): void {
  try {
    store?.removeItem(CHECKPOINT_KEY)
  } catch {
    /* storage unavailable: nothing to clear */
  }
}

let lastWritten: string | null = null
let lastSessionId: string | undefined

/**
 * Keep the stored checkpoint in step with the session. Called on every store
 * change, so it writes only when the kept fields actually changed. Completion
 * clears it; an empty store does NOT, because after a reload the store starts
 * empty before Welcome has had the chance to offer the checkpoint back.
 *
 * A new session in the store supersedes a checkpoint of another one. Some flows
 * end a paid session without cleanupSession (bahela's camera-recovery nav-home,
 * its back/restart buttons), and that checkpoint must not be offered after a
 * crash during the next guest's session.
 */
export function syncCheckpoint(s: CheckpointSource, store = defaultStore()): void {
  if (!store) return
  try {
    if (s.id && s.id !== lastSessionId) {
      lastSessionId = s.id
      const current = readCheckpoint(store)
      if (current && current.sessionId !== s.id) {
        store.removeItem(CHECKPOINT_KEY)
        lastWritten = null
      }
    }
    if (s.id && s.timeline?.isCompleted) {
      const current = readCheckpoint(store)
      if (current?.sessionId === s.id) {
        store.removeItem(CHECKPOINT_KEY)
        lastWritten = null
      }
      return
    }
    const cp = buildCheckpoint(s)
    if (!cp) return
    const signature = JSON.stringify({ ...cp, savedAt: 0 })
    if (signature === lastWritten) return
    store.setItem(CHECKPOINT_KEY, JSON.stringify(cp))
    lastWritten = signature
  } catch {
    /* quota or privacy mode: resuming is a convenience, never block the session */
  }
}

/** Test hook. */
export function resetCheckpointWriterState(): void {
  lastWritten = null
  lastSessionId = undefined
}

/** Milliseconds left before this checkpoint stops being offered (never below 0). */
export function msUntilExpiry(cp: SessionCheckpoint, now = Date.now()): number {
  return Math.max(0, cp.savedAt + CHECKPOINT_MAX_AGE_MS - now)
}

const PACKAGE_LABELS: Record<string, string> = {
  strip: 'strip',
  print: 'print',
  gif: 'GIF',
  video: 'video',
  strip_live: 'video',
  strip_story: 'video',
  recording: 'video',
  session_recording: 'video',
  digital: 'digital',
  pax: 'pax',
  ai_effect: 'AI'
}

/**
 * What the guest bought, short enough to recognise at a glance ("2× strip, video").
 * The paying guest knows it; a stranger who walked up does not. Unknown types and
 * zero quantities are skipped, and an empty result means "show nothing".
 */
export function describePackages(transaction: Record<string, unknown>): string {
  const packages = Array.isArray(transaction.selectedPackages) ? transaction.selectedPackages : []
  const parts: string[] = []
  for (const p of packages as Array<{ type?: string; quantity?: number }>) {
    const label = p?.type ? PACKAGE_LABELS[p.type] : undefined
    if (!label) continue
    const qty = Number(p.quantity ?? 1)
    if (!(qty > 0)) continue
    const part = qty > 1 ? `${qty}× ${label}` : label
    if (!parts.includes(part)) parts.push(part)
  }
  return parts.join(', ')
}

/** Whether Welcome should offer this checkpoint back to the guest. */
export function isOfferable(
  cp: SessionCheckpoint | null,
  opts: { projectId: string | undefined; activeSessionId: string | undefined; now?: number }
): cp is SessionCheckpoint {
  if (!cp) return false
  if (opts.activeSessionId) return false
  if (!opts.projectId || cp.projectId !== opts.projectId) return false
  return (opts.now ?? Date.now()) - cp.savedAt <= CHECKPOINT_MAX_AGE_MS
}
